<template>
  <div>
    <b-form
      inline
      class="mb-2"
    >
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        class="d-inline-block mr-1 mb-1"
        @click="createNew"
      >
        {{ $t('Add') }}
      </b-button>

      <b-form-input
        v-model="searchTerm"
        :placeholder=" $t('message.SearchPlaceholder')"
        type="text"
        class="d-inline mb-1"
      />

      <b-dropdown
        v-if="selectedRecords.length>0"
        id="dropdown-actions"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        :text="$t('Actions')"
        variant="primary"
        class="ml-1 mb-1"
      >
        <b-dropdown-item
          variant="info"
          @click="runSelectedRecords()"
        >
          <feather-icon
            icon="PlayIcon"
          />
          {{ $t('Run Selected Conditions') }}

        </b-dropdown-item>
        <b-dropdown-item
          variant="success"
          @click="setStatusSelectedRecords(true)"
        >
          <feather-icon
            icon="EyeIcon"
          />
          {{ $t('Set as Active') }}

        </b-dropdown-item>
        <b-dropdown-item
          variant="warning"
          @click="setStatusSelectedRecords(false)"
        >
          <feather-icon
            icon="EyeOffIcon"
          />
          {{ $t('Set as Passive') }}

        </b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item
          variant="danger"
          @click="confirmDelete"
        >
          <feather-icon
            icon="Trash2Icon"
          />
          {{ $t('Delete') }}

        </b-dropdown-item>
      </b-dropdown>

    </b-form>

    <!-- table -->
    <vue-good-table
      style-class="vgt-table striped"
      :columns="columns"
      :rows="data"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'id', type: 'desc'}
      }"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength,
        mode: 'records',
        perPage: 10,
        perPageDropdown: [10, 50, 100],
        dropdownAllowAll: false,
      }"
      @on-selected-rows-change="selectedRowsChanged"
    >

      <template
        slot="table-column"
        slot-scope="props"
      >
        <span>
          {{ $t(props.column.label) }}
        </span>
      </template>

      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: id -->
        <span
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <b-link @click="edit(props.row)">
            <feather-icon
              icon="EditIcon"
            />

          </b-link>
        </span>
        <span
          v-else-if="props.column.field === 'status'"
          class="text-nowrap"
        >
          <b-badge
            pill
            :variant="`light-${resolveStatus(props.row[props.column.field])}`"
            class="text-capitalize"
          >
            {{ props.row[props.column.field] }}
          </b-badge>
        </span>
        <span
          v-else-if="props.column.field === 'filterConfigJson'"
          class="text-nowrap"
        >
          <div
            v-for="(filterItem,index) in props.row.filterConfig.filters"
            :key="index"
          >
            <template v-if="filterItem.operator">
              {{ filterItem.operator }}
            </template>
            <template v-else>
              <strong class="mr-1">{{ filterItem.filterQuestion.questionText }} = </strong>
              <b-badge
                v-for="(value,idx) in filterItem.filterValues"
                :key="idx"
                variant="primary"
              >{{ value.optionText }}</b-badge>
              <b-badge
                v-for="(value,idx) in filterItem.filterValuesText"
                :key="idx"
                variant="primary"
              >{{ value }}</b-badge>
            </template>
          </div>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

    </vue-good-table>

    <modal-item
      item-type="hotalertcondition"
      :edit-item="edit_item"
      @submit="getConditions"
      @clear="edit_item=null"
    />

  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'

import {
  BLink, BFormInput, BForm, BButton, BDropdown, BDropdownItem, BDropdownDivider, BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import ModalItem from './ModalItem.vue'

export default {
  components: {
    VueGoodTable,
    BLink,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormInput,
    BForm,
    BButton,
    BBadge,
    ModalItem,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: [],
      columns: [
        {
          label: '#',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
        },
        {
          label: 'Project',
          field: 'projectName',
        },
        {
          label: 'HotAlert',
          field: 'hotAlertReason',
        },
        {
          label: 'Auto Assign By',
          field: 'autoAssignQuestionName',
        },
        {
          label: 'Auto Assign To',
          field: 'autoAssignEmail',
        },
        {
          label: 'Filter',
          field: 'filterConfigJson',
        },
        {
          label: 'Alert Count',
          field: 'alertCount',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '120px',
        },
        {
          label: 'Status',
          field: 'status',
        },
      ],
      pageLength: 10,
      searchTerm: '',
      selectedRecords: [],
      edit_item: null,
    }
  },
  created() {
    this.getConditions()
  },
  methods: {
    resolveStatus(status) {
      if (status) return 'success'
      if (!status) return 'danger'
      return 'secondary'
    },
    getConditions() {
      this.edit_item = null
      this.$bvModal.hide('add-item')
      useJwt.axiosIns.get('hotalert/condition')
        .then(response => {
          if (response.data) {
            this.data = response.data
            this.data.forEach(element => {
              const el = element
              if (el.filterConfigJson) {
                el.filterConfig = JSON.parse(element.filterConfigJson)
              }
            })
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    edit(item) {
      this.edit_item = JSON.parse(JSON.stringify(item))
      this.$bvModal.show('add-item')
      // this.$bvModal.show('add-page')
    },
    createNew() {
      this.$bvModal.show('add-item')
      // this.$bvModal.show('add-page')
    },
    selectedRowsChanged(vals) {
      this.selectedRecords = vals.selectedRows.map(x => x.id)
    },

    confirmDelete() {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t('You are about to delete selected record(s)... If you delete conditions, all historical hot alert data will be deleted!!!'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteSelectedRecords()
        }
      })
    },

    deleteSelectedRecords() {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.delete(
        `hotalert/condition/${x}`,
      ))

      Promise.all(promises).then(() => {
        this.getConditions()
        // show message
        this.$swal({
          icon: 'success',
          title: 'Deleted!',
          text: `${this.selectedRecords.length} record(s) has been deleted.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    setStatusSelectedRecords(status) {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.patch(
        `hotalert/condition/${x}/setStatus/${status}`,
      ))

      Promise.all(promises).then(() => {
        this.getConditions()
        // show message
        this.$swal({
          icon: 'success',
          title: 'Success!',
          text: `${this.selectedRecords.length} record(s) has been set status:${status}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },

    runSelectedRecords() {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.post(
        `hotalert/condition/run/${x}`,
      ))
      Promise.all(promises).then(() => {
        // show message
        this.getConditions()
        this.$swal({
          icon: 'success',
          title: 'Success!',
          text: `${this.selectedRecords.length} condition(s) completed.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },

  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
